<template>
    <v-dialog
        v-model="dialog"
        max-width="820"
    >
        <v-card v-if="['finished', 'send'].includes(mixin_bulk_message_status.state)">
            <v-card-title class="headline">
                {{
                    mixin_bulk_message_status.state === 'send' ? 'Sending Bulk Message' : 'Finished Sending Bulk Message'
                }}
            </v-card-title>

            <v-card-text>
                <p v-if="mixin_bulk_message_status.state === 'send'">
                    Time elapsed: {{ mixin_time_elapsed }}, estimated total time: {{ mixin_time_total }}.
                </p>

                <v-row class="mt-1">
                    <v-col
                        v-if="mixin_bulk_message_status.state === 'send'"
                        cols="12"
                    >
                        <v-progress-linear
                            :value="mixin_percent_total"
                            height="20"
                            color="primary"
                            background-color="grey lighten-3"
                        >
                            <strong>{{ mixin_percent_total }}%</strong>
                        </v-progress-linear>
                    </v-col>

                    <v-col
                        v-if="mixin_bulk_message_status.state === 'finished'"
                        cols="12"
                    >
                        <v-alert
                            type="success"
                            border="left"
                            colored-border
                            elevation="0"
                            class="shaded-light mb-2"
                        >
                            Bulk message sending complete.
                        </v-alert>
                    </v-col>
                </v-row>

                <v-row class="mt-2">
                    <v-col
                        cols="6"
                        md="3"
                    >
                        <v-card
                            outlined
                            class="pa-3 text-center"
                        >
                            <div class="text-h4 stat-display">{{
                                mixin_bulk_message_status.count_pending?.toLocaleString() || 0 }}
                            </div>
                            <div class="text-caption text-uppercase">Pending</div>
                        </v-card>
                    </v-col>
                    <v-col
                        cols="6"
                        md="3"
                    >
                        <v-card
                            outlined
                            class="pa-3 text-center"
                        >
                            <div class="text-h4 error--text stat-display">{{ mixin_filtered_total?.toLocaleString() || 0
                            }}</div>
                            <div class="text-caption text-uppercase">Filtered</div>
                        </v-card>
                    </v-col>
                    <v-col
                        cols="6"
                        md="3"
                    >
                        <v-card
                            outlined
                            class="pa-3 text-center"
                        >
                            <div class="text-h4 error--text stat-display">{{ mixin_failed_total?.toLocaleString() || 0
                            }}</div>
                            <div class="text-caption text-uppercase">Failed</div>
                        </v-card>
                    </v-col>
                    <v-col
                        cols="6"
                        md="3"
                    >
                        <v-card
                            outlined
                            class="pa-3 text-center"
                        >
                            <div class="text-h4 success--text stat-display">{{
                                mixin_bulk_message_status.count_sent?.toLocaleString()
                                || 0 }}</div>
                            <div class="text-caption text-uppercase">Sent</div>
                        </v-card>
                    </v-col>
                </v-row>

                <v-alert
                    v-if="should_show_filter_warning"
                    type="warning"
                    outlined
                    class="mt-4"
                    color="primary"
                    icon="mdi-alert"
                >
                    Your bulk message {{ mixin_bulk_message_status.state === 'send' ? 'is experiencing' : 'experienced' }} a high filter rate ({{ mixin_filter_rate_percent }}%) -- this
                    could be by design, or it could be an issue with your settings.
                </v-alert>

                <p class="mt-4 mb-0">
                    <span v-if="mixin_bulk_message_status.state === 'send'">
                        Messages will continue to send in the background, you do not need to remain on this
                        screen.<br />
                    </span>
                    Feel free to go monitor the replies to this message by clicking <a
                        v-on:click="generateEndpointLink">here.</a><br />
                    <span v-if="mixin_bulk_message_status.state === 'send'">
                        You can continue to monitor the send process <a @click="onViewSendSummary">here.</a>
                    </span>
                    <span v-if="mixin_bulk_message_status.state === 'finished'">
                        You can view further details on the send process <a @click="onViewSendSummary">here.</a>
                    </span>
                </p>
            </v-card-text>

            <v-card-actions>
                <template v-if="mixin_bulk_message_status.state === 'send'">
                    <v-btn
                        text
                        @click="onClickHome"
                    >
                        Home
                    </v-btn>

                    <v-spacer />

                    <v-btn
                        color="primary darken-1"
                        text
                        @click="onClickPause"
                    >
                        Pause
                    </v-btn>
                </template>

                <template v-if="mixin_bulk_message_status.state === 'finished'">
                    <v-spacer />

                    <v-btn
                        color="primary darken-1"
                        text
                        @click="onOk"
                    >
                        Close
                    </v-btn>
                </template>
            </v-card-actions>
        </v-card>

        <v-card v-if="mixin_bulk_message_status.state == 'paused'">
            <v-card-title class="headline">
                Sending is currently paused
            </v-card-title>

            <v-card-text>

                <p>No further messages will be sent until you click "resume sending"</p>

                <p><b>Progress so far</b></p>

                <p>
                    {{ mixin_recipients_total - mixin_bulk_message_status.count_pending }} of {{
                        mixin_recipients_total }} messages
                    ({{ mixin_percent_total }}%) had been sent before pausing.
                </p>

                <p><b>Need to make a change?</b></p>

                <p>
                    Cancel this bulk message and create a new one. Tip: you can export this recipient list, delete the
                    recipients that received this message, and upload the remaining recipients to your new bulk message.
                </p>

                <p><b>Don't want to send any more?</b></p>

                <p>
                    Type "Cancel" below to make the "Cancel Bulk Message" option appear and then select it. No one will
                    be able to resume this bulk message again. <b>This cannot be undone.</b>
                </p>

                <v-text-field
                    label="Cancel Bulk Message"
                    hint="Type cancel"
                    persistent-hint
                    v-model="cancel_text"
                />

            </v-card-text>

            <v-card-actions>

                <v-btn
                    text
                    @click="onClickHome"
                >
                    Home
                </v-btn>

                <v-spacer></v-spacer>

                <v-btn
                    color="primary darken-1"
                    text
                    @click="onClickResume"
                    v-if="cancel_text == null || cancel_text.trim().toLowerCase() != 'cancel'"
                >
                    Resume Sending
                </v-btn>
                <v-btn
                    color="primary darken-1"
                    text
                    @click="onClickCancel"
                    v-else
                >
                    Cancel Bulk Message
                </v-btn>

            </v-card-actions>
        </v-card>

        <v-card v-if="mixin_bulk_message_status.state == 'cancelled'">
            <v-card-title class="headline">
                Bulk Message Sending Cancelled
            </v-card-title>

            <v-card-text>
                <p>
                    Bulk message cancelled. Reason: {{ mixin_bulk_message_status.cancellation_reason }}
                </p>
            </v-card-text>

            <v-card-actions>

                <v-spacer></v-spacer>

                <v-btn
                    color="primary darken-1"
                    text
                    @click="onOk"
                >
                    Close
                </v-btn>

            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import _ from 'lodash';
import E164 from '@/js/E164.js';
import { v4 as uuidv4 } from 'uuid';
import BulkMessageSendStatusMixin from '@/mixins/bulk-message-send-status.js';

export default {
    mixins: [BulkMessageSendStatusMixin],

    data() {
        return {
            dialog: false,
            from_endpoint: null,
            current_time: new Date().getTime(),
            interval: null,
            cancel_text: null,
        }
    },

    mounted() {
        this.interval = window.setInterval(this.updateTime, 1000);
    },

    beforeDestroy() {
        if (this.interval != null) {
            window.clearInterval(this.interval);
            this.interval = null;
        }
    },

    methods: {

        updateTime() {
            this.current_time = new Date().getTime();
        },

        updateProgress(bulk_message_status, from_endpoint) {
            const just_finished = this.mixin_bulk_message_status.state === 'send' && bulk_message_status.state === 'finished';

            this.mixin_bulk_message_status = _.cloneDeep(bulk_message_status);
            this.current_time = new Date().getTime();
            this.from_endpoint = from_endpoint;

            if (bulk_message_status.state == 'send' || bulk_message_status.state == 'paused') {
                if (this.dialog == false)
                    this.cancel_text = null;

                this.dialog = true;
            }
        },

        onOk() {
            this.$emit('completed');
            this.dialog = false;
        },

        generateEndpointLink() {
            let url = this.$router.resolve({
                'name': 'home-routed',
                'params': {
                    'endpoint': E164.slimEndpoint(this.from_endpoint),
                    'conversation_id': 'none',
                }
            });

            window.open(url.href, '_blank');
        },

        onViewSendSummary() {
            let url = this.$router.resolve({
                'name': 'view-bulk-message-send-summary',
                'params': {
                    'organization_id': this.$route.params.organization_id,
                    'bulk_message_id': this.mixin_bulk_message_status.bulk_message_id,
                }
            });

            window.open(url.href, '_blank');
        },

        onClickPause() {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/bulk/pause',

                'params': {
                    'bulk_message_id': this.mixin_bulk_message_status.bulk_message_id,
                    'user_action_id': uuidv4()
                },

                'on_success': (data) => {
                    this.mixin_bulk_message_status.state = 'paused';
                },

                'on_error': 'Unable to pause bulk message',
            });
        },

        onClickResume() {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/bulk/resume',

                'params': {
                    'bulk_message_id': this.mixin_bulk_message_status.bulk_message_id,
                    'user_action_id': uuidv4()
                },

                'on_success': (data) => {
                    this.mixin_bulk_message_status.state = 'send'; // to immediately change the dialog
                },

                'on_error': 'Unable to resume bulk message',
            });
        },

        onClickCancel() {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/bulk/cancel',

                'params': {
                    'bulk_message_id': this.mixin_bulk_message_status.bulk_message_id,
                },

                'on_success': (data) => {
                    this.mixin_bulk_message_status.state = 'cancelled'; // to immediately change the dialog
                    this.$rc.snackbar('Sending this bulk message has been cancelled');
                    this.dialog = false;

                    window.setTimeout(
                        () => {
                            this.$emit('completed');
                        },
                        1000
                    );

                    window.setTimeout(
                        () => {
                            this.$emit('completed');
                        },
                        5000
                    );

                    window.setTimeout(
                        () => {
                            this.$emit('completed');
                        },
                        10000
                    );
                },

                'on_error': 'Unable to cancel bulk message',
            });
        },

        onClickHome() {
            this.$router.push({
                'name': 'home',
                'params': {
                    'organization_id': this.$rc.$state.organization_id,
                }
            }).catch(e => { }); // swallow any exception
        },
    },

    computed: {
        should_show_filter_warning() {
            return this.mixin_messages_processed >= 50 && this.mixin_filter_rate_percent >= 25;
        },
    },
}
</script>

<style lang="scss" scoped>
.stat-display {
    overflow-wrap: normal;
}
</style>